import { useState, useEffect } from 'react';
import {
  IShopParams,
  IShopWaitingTask,
  IGetNewShopOfStaffParams,
  ITotalShopQRKpiSatisfy,
  IShopApproach,
  IGetBaseParams,
} from 'interfaces';
import { shopServices } from 'services';
import { ETicketStatus } from 'constants/task';
import { shopConstant, commonConstants } from 'constants/index';
import { defaultPage, defaultPageSize } from 'constants/pagination';
import { INewShop } from 'interfaces/report';
import { pickBy } from 'lodash';

const { STATUS_KPI } = shopConstant;
const { DEFAULT_PARAMS } = commonConstants;

const useShop = (defaultParams: IShopParams) => {
  const [loading, setLoading] = useState<boolean>(false);
  const [params, setParams] = useState<IShopParams>(defaultParams);
  const [shops, setShops] = useState<IShopWaitingTask[]>([]);
  const [totalExpired, setTotalExpired] = useState<number>(0);
  const [totalGoingToBeOverdue, setTotalGoingToBeOverdue] = useState<number>(0);
  const [totalItems, setTotalItems] = useState<number>(0);

  const fetchShops = async () => {
    try {
      setLoading(true);
      const fetchParams = { ...params };
      if (fetchParams.ticketStatus === ETicketStatus.ALL) {
        delete fetchParams.ticketStatus;
      }
      const result = await shopServices.getShops(fetchParams);

      const { data } = result;
      // set data tickets for showing on the table
      setShops(data.items);
      setTotalExpired(data.totalTicketExpired);
      setTotalGoingToBeOverdue(data.totalTicketGoingToBeOverdue);
      setTotalItems(data.totalObjectToTakeCareItems);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchShops().then();
  }, [params]);

  return {
    loading,
    setLoading,
    params,
    setParams,
    shops,
    totalExpired,
    totalGoingToBeOverdue,
    totalItems,
    fetchShops,
  };
};

const useShopV2 = (defaultParams: IShopParams) => {
  const [loading, setLoading] = useState<boolean>(false);
  const [params, setParams] = useState<IShopParams>(defaultParams);
  const [shops, setShops] = useState<IShopWaitingTask[]>([]);
  const [totalExpired, setTotalExpired] = useState<number>(0);
  const [totalGoingToBeOverdue, setTotalGoingToBeOverdue] = useState<number>(0);
  const [totalItems, setTotalItems] = useState<number>(0);

  const fetchShops = async () => {
    try {
      setLoading(true);
      const fetchParams = { ...params };
      if (fetchParams.ticketStatus === ETicketStatus.ALL) {
        delete fetchParams.ticketStatus;
      }
      const query = pickBy(
        { ...fetchParams },
        e => e !== undefined && e !== null && typeof e == 'string' && e !== ''
      );
      const result = await shopServices.getShopsV2(query);

      const { data } = result;
      // set data tickets for showing on the table
      setShops(data.items);
      setTotalExpired(data.totalTicketExpired);
      setTotalGoingToBeOverdue(data.totalTicketGoingToBeOverdue);
      setTotalItems(data.totalObjectToTakeCareItems);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchShops().then();
  }, [params]);

  return {
    loading,
    setLoading,
    params,
    setParams,
    shops,
    totalExpired,
    totalGoingToBeOverdue,
    totalItems,
    fetchShops,
  };
};

const useNewListShopOfStaff = () => {
  const initParams = {
    kpiStatus: STATUS_KPI.All.value,
    pageSize: defaultPageSize,
    page: defaultPage,
  };

  const [params, setParams] = useState<IGetNewShopOfStaffParams>(initParams);
  const [newShops, setNewShops] = useState<INewShop[]>([]);
  const [totalShop] = useState<ITotalShopQRKpiSatisfy>();
  const [hasMore, setHasMore] = useState(true);
  const [loading, setLoading] = useState(false);

  const getNewShops = async () => {
    try {
      if (params.page === defaultPage) setLoading(true);

      const res = await shopServices.getNewShopsOfStaff(params);
      setHasMore(res.shopNews.length === defaultPageSize);
      if (params.page === defaultPage) setNewShops(res.shopNews);
      else {
        setNewShops([...newShops, ...res.shopNews]);
      }
    } finally {
      setLoading(false);
    }
  };

  // const getTotalShop = async () => {
  //   const res = await shopServices.getTotalKpiSatisfy();
  //   if (res) {
  //     setTotalShop(res);
  //   }
  // };

  useEffect(() => {
    getNewShops();
  }, [params]);

  // useEffect(() => {
  //   getTotalShop();
  // }, []);

  return {
    newShops,
    setParams,
    params,
    hasMore,
    loading,
    getNewShops,
    initParams,
    totalShop,
  };
};

const useListShopApproach = () => {
  const [loading, setLoading] = useState<boolean>(false);
  const [params, setParams] = useState<IGetBaseParams>(DEFAULT_PARAMS);
  const [listShopApproach, setListShopApproach] = useState<IShopApproach[]>([]);
  const [hasMore, setHasMore] = useState(true);
  const [totalShop, setTotalShop] = useState<number>(0);
  const [loadingChangeStatus, setLoadingChangeStatus] = useState<boolean>(
    false
  );

  const getListShopApproach = async () => {
    try {
      if (params.page === defaultPage) setLoading(true);
      const res = await shopServices.getListShopApproach(params);
      setTotalShop(res.pagination.totalItems);
      setHasMore(res.shops.length === defaultPageSize);
      if (params.page === defaultPage) setListShopApproach(res.shops);
      else {
        const moreShop = [...listShopApproach, ...res.shops];
        setListShopApproach(moreShop);
      }
    } finally {
      setLoading(false);
    }
  };

  const changeStatusToAddProfile = async (
    merchantId: number,
    reason: string
  ) => {
    try {
      setLoadingChangeStatus(true);
      await shopServices.changeStatusToAddProfile(merchantId, { reason });
      await getListShopApproach();
    } finally {
      setLoadingChangeStatus(false);
    }
  };

  useEffect(() => {
    getListShopApproach();
  }, [params]);

  return {
    params,
    setParams,
    hasMore,
    loading,
    listShopApproach,
    totalShop,
    changeStatusToAddProfile,
    loadingChangeStatus,
  };
};

export default {
  useShop,
  useShopV2,
  useNewListShopOfStaff,
  useListShopApproach,
};
